/**
 * Variant grouping utilities for catalog items.
 *
 * Groups items that share a common prefix (e.g. "Design - Small", "Design - Large")
 * into a single card with selectable variant chips.
 *
 * Algorithm: common-prefix grouping with multi-separator support.
 * Recognized separators (in priority order):
 *   1. Trailing parenthetical:  "Design: Cart (w/ Implementation)" → "Design: Cart"
 *   2. Dash (with en/em dash):  "SEO Audit - Small"                → "SEO Audit"
 *   3. Colon:                   "UX Implementation: XXL"            → "UX Implementation"
 *
 * Items with no separator, or whose base name is unique within their service
 * line, remain standalone.
 */
import { CatalogItem, normalizeServiceLine } from './types'

export interface ItemGroup {
  baseName: string
  items: CatalogItem[]
  serviceLine: string
  isVariantGroup: boolean // true when items.length > 1
}

/**
 * Extract the base name using multi-separator common-prefix logic.
 *
 * Examples:
 *   "SEO Audit - Small"                    → "SEO Audit"
 *   "Content Strategy - Blog - Monthly"    → "Content Strategy - Blog"
 *   "Basic Design: Homepage"               → "Basic Design"
 *   "Design: Cart (w/ Implementation)"     → "Design"
 *   "Email Marketing"                      → "Email Marketing"  (no separator)
 */
export function getBaseName(title: string): string {
  let name = title.trim()

  // Step 1: Strip trailing parenthetical modifier
  name = name.replace(/\s*\([^)]*\)\s*$/, '')

  // Step 2: Find the last separator — dash (` - ` / ` – ` / ` — `) or colon (`: `)
  const sepPattern = /\s+[-–—]\s+|:\s+/g
  let lastIndex = -1
  let match
  while ((match = sepPattern.exec(name)) !== null) {
    lastIndex = match.index
  }

  if (lastIndex <= 0) return name
  return name.slice(0, lastIndex).trim()
}

/**
 * Extract the variant label — the portion after the base name.
 *
 * Examples:
 *   getVariantLabel("SEO Audit - Small", "SEO Audit")       → "Small"
 *   getVariantLabel("Design: Cart (w/ Implementation)", "Design") → "Cart (w/ Implementation)"
 */
export function getVariantLabel(title: string, baseName: string): string {
  if (title === baseName) return title
  const rest = title
    .slice(baseName.length)
    .replace(/^\s*[-–—]\s*/, '') // strip leading dash
    .replace(/^:\s*/, '') // strip leading colon
    .trim()
  return rest || title
}

/**
 * Group items by baseName + serviceLine, sorting variants by points ascending.
 *
 * Two-pass algorithm:
 * 1. Group all items by their (baseName, serviceLine) key.
 * 2. Flatten any group with only 1 item — standalone card, not a variant group.
 */
export function groupItemsByVariant(items: CatalogItem[]): ItemGroup[] {
  // Pass 1: group by (baseName, serviceLine)
  const groups = new Map<string, CatalogItem[]>()

  items.forEach(item => {
    const baseName = getBaseName(item.title)
    const key = `${baseName}|||${normalizeServiceLine(item.service_line)}`
    const group = groups.get(key) || []
    group.push(item)
    groups.set(key, group)
  })

  // Pass 2: build result, collapsing single-item groups back to full name
  return Array.from(groups.entries()).map(([key, groupItems]) => {
    const baseName = key.split('|||')[0]
    const serviceLine = normalizeServiceLine(groupItems[0].service_line)

    if (groupItems.length === 1) {
      return {
        baseName: groupItems[0].title,
        items: groupItems,
        serviceLine,
        isVariantGroup: false,
      }
    }

    return {
      baseName,
      items: groupItems.sort((a, b) => (a.points || 0) - (b.points || 0)),
      serviceLine,
      isVariantGroup: true,
    }
  })
}
